/** The comments submitted on a document, each with what the agents did with it.
 * See docs/Y2026-Q4/design-review-mode.md section 3. */
import { useState } from "react";
import { Link } from "react-router-dom";

import type { ReviewPhase } from "../api/hooks/useDashboard";
import {
  type ReviewComment,
  type ReviewCommentsScope,
  useReviewComments,
} from "../api/hooks/useReview";
import { formatWhen } from "../utils/formatWhen";
import { anchorLabel } from "../utils/reviewState";
import { conversationHref } from "../utils/reviewRunHref";
import { agentDisplayLabel } from "../utils/runParticipants";
import { Spinner } from "./Spinner";
import styles from "./Review.module.css";

const STORED_LABEL: Record<string, string> = {
  state_document: "the state document",
  operator_instructions: "Operator instructions",
  status_row: "the status row",
  rule: "a rule",
  client_kb: "the client knowledge base",
  email: "the email",
  decision: "a decision",
  project_summary: "the project summary",
  project_attribute: "a project attribute",
  register: "the document register",
  feedback_question: "the Agent feedback question",
};

/** What has become of a comment, as the response half of its card says it. Only a
 * settled comment or a question carries text: the agent's reply in the conversation
 * says what is being changed. */
const responseOf = (
  comment: ReviewComment,
  phase: ReviewPhase | undefined,
  workedBy: readonly string[],
): { status: string; label: string; text: string } => {
  const settler = comment.settled_by?.replace(/^agent:/, "") ?? "";
  const others = workedBy.filter((a) => a !== settler).map(agentDisplayLabel);
  // On a pass the Manager settles every comment; who worked on it is the whole team.
  const agent =
    (settler ? agentDisplayLabel(settler) : "The agent") +
    (others.length ? `, with ${others.join(" and ")}` : "");
  if (phase === "queued") {
    return {
      status: "open",
      label: "Queued",
      text: "Sent to the agent once it has finished with your earlier comments.",
    };
  }
  if (phase === "answering") return { status: "processing", label: "Being applied now", text: "" };
  switch (comment.status) {
    case "actioned":
      return { status: "actioned", label: `Applied by ${agent}`, text: comment.response ?? "" };
    case "declined":
      return { status: "declined", label: `Not applied by ${agent}`, text: comment.response ?? "" };
    case "needs_input":
      return {
        status: "needs_input",
        label: "Needs your input",
        text: `${agent} asks: ${comment.response ?? ""}`,
      };
    case "withdrawn":
      return { status: "withdrawn", label: "Withdrawn", text: "" };
    default:
      return { status: "open", label: "Not answered yet", text: "" };
  }
};

/** One submitted comment: what the operator wrote, then what the system did with it. */
export const ReviewCommentItem = ({
  comment,
  projectId,
  phase,
  linkConversation = true,
  workedBy = [],
}: {
  comment: ReviewComment;
  projectId: string | null;
  /** What is happening to it now, if it is in flight. */
  phase?: ReviewPhase;
  /** The agents that took part in the run that settled it. */
  workedBy?: readonly string[];
  /** False inside the conversation itself. */
  linkConversation?: boolean;
}) => {
  const conversation = conversationHref(comment.conversation_id, projectId);
  const response = responseOf(comment, phase, workedBy);
  return (
    <li className={styles.thread}>
      <div className={styles.submitted}>
        <div className={styles.threadHead}>
          <span>
            <strong>{comment.author_email || "An operator"}</strong> commented
          </span>
          <span className={styles.meta}>
            {formatWhen(comment.created_at)} · on {anchorLabel(comment.anchor)}
          </span>
        </div>
        {comment.anchor.quote && (
          <blockquote className={styles.quote}>{comment.anchor.quote}</blockquote>
        )}
        <p className={styles.commentBody}>{comment.body}</p>
      </div>
      <div className={`${styles.response} ${styles[`response_${response.status}`] ?? ""}`}>
        <div className={styles.responseHead}>
          {response.status === "processing" && <Spinner label={response.label} />}
          <strong>{response.label}</strong>
        </div>
        {response.text && <p className={styles.responseText}>{response.text}</p>}
        {comment.stored_in && comment.status === "actioned" && (
          <p className={styles.meta}>
            Stored in {STORED_LABEL[comment.stored_in] ?? comment.stored_in}
          </p>
        )}
      </div>
      {conversation && linkConversation && (
        <span className={styles.threadLinks}>
          <Link className={styles.link} to={conversation}>
            {comment.status === "needs_input"
              ? "Answer in the conversation"
              : "Open the conversation"}
          </Link>
        </span>
      )}
    </li>
  );
};

/** The comments already submitted on this document, open first: one line saying how many
 * and where, the comments themselves behind "Show", so a comment going in or settling
 * moves the page by no more than that line (Noaa, 29 Oct 2026). */
export const ReviewThreads = ({
  scope,
  projectId,
  withSettled = false,
}: {
  scope: ReviewCommentsScope;
  projectId: string | null;
  /** Offer the settled ones too. A project's settled comments live in its review
   * conversations instead, so its pages show only what is still in flight. */
  withSettled?: boolean;
}) => {
  const { data, error } = useReviewComments(scope);
  const [showSettled, setShowSettled] = useState(false);
  const [expanded, setExpanded] = useState(false);
  if (error) return <p className={styles.error}>Could not load the review comments.</p>;
  const comments = data?.comments ?? [];
  if (comments.length === 0) return null;
  const phases = data?.phases ?? {};
  const open = comments.filter((c) => c.status === "open" || c.status === "needs_input");
  const settled = withSettled
    ? comments.filter((c) => c.status !== "open" && c.status !== "needs_input").reverse()
    : [];
  if (open.length === 0 && settled.length === 0) return null;
  const inFlight = open.filter((c) => phases[c.comment_id]).length;
  const asking = open.filter((c) => c.status === "needs_input").length;
  const plural = (n: number) => (n === 1 ? "review comment is" : "review comments are");
  const shown = [...(expanded ? open : []), ...(showSettled ? settled : [])];
  const conversations = [
    ...new Set(open.map((c) => c.conversation_id).filter((id): id is string => !!id)),
  ];
  return (
    <section className={styles.threads}>
      <h3 className={styles.threadsTitle}>
        {inFlight > 0 ? (
          <>
            <span className={styles.spinnerGap}>
              <Spinner label="Being applied" />
            </span>
            {inFlight} {plural(inFlight)} being applied
          </>
        ) : (
          `Review comments · ${open.length} open`
        )}
        {asking > 0 && ` · ${asking} need${asking === 1 ? "s" : ""} your input`}
        {conversations.map((id, i) => {
          const href = conversationHref(id, projectId);
          return (
            href && (
              <Link key={id} className={styles.link} to={href}>
                {asking > 0 ? "Answer in the conversation" : "Open the conversation"}
                {conversations.length > 1 ? ` ${i + 1}` : ""}
              </Link>
            )
          );
        })}
        {open.length > 0 && (
          <button
            type="button"
            className={styles.link}
            aria-expanded={expanded}
            onClick={() => setExpanded(!expanded)}
          >
            {expanded ? "Hide" : `Show ${open.length === 1 ? "it" : `all ${open.length}`}`}
          </button>
        )}
        {settled.length > 0 && (
          <button type="button" className={styles.link} onClick={() => setShowSettled(!showSettled)}>
            {showSettled ? "Hide settled" : `Show ${settled.length} settled`}
          </button>
        )}
      </h3>
      {shown.length > 0 && (
        <ul className={styles.threadList}>
          {shown.map((c) => (
            <ReviewCommentItem
              key={c.comment_id}
              comment={c}
              projectId={projectId}
              phase={phases[c.comment_id]}
              linkConversation={false}
              workedBy={data?.worked_by?.[`${c.settled_by_run_id}:${c.project_id}`]}
            />
          ))}
        </ul>
      )}
    </section>
  );
};
